import { LogModule } from '@/data/config';
import { isBrowser } from './detector';
import { loggerError } from './logger';
import { getDomain } from './url/common';
import { isBoolean, isNumber, isObject, isString } from './validate';

const parseToJSON = (val: string, enableParsing: boolean) => {
  if (!enableParsing) return val;
  try {
    return JSON.parse(val);
  } catch (err) {
    return val;
  }
};

interface ISetCookieOptions {
  time?: number;
  domain?: string;
  path?: string;
  cookies?: string;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: string;
  isNeedSetCookies?: boolean;
}

/**@method setCookie  存储cookie
 * 第三个参数可以以对象的形式传入 time, domain, path, httpOnly, secure, sameSite, isNeedSetCookies 为属性传入
 * @param {string} name cookie名字
 * @param {string} value cookie值
 * @param {number} time  cookie过期时间格式1970/01/01
 * @param {string} domain cookie所在的域，默认为请求的地址，如果传入 never 则代表不加 domain 字段,只要传了 domain 就会覆盖默认的域名
 * @param {string} path cookie所在的目录，默认为/，就是根目录。
 * @return {void}
 */
export function setCookie(
  name: string,
  value: string | number,
  time?: number | ISetCookieOptions,
  domain = getDomain(),
  path = '/'
): void | string {
  value = isObject(value) ? JSON.stringify(value) : value;

  let _cookies; // cookies 为空
  let _time = time;
  let httpOnly;
  let secure;
  let sameSite;
  let isNeedSetCookies = true;
  // 当time 传入对象时，重置变量名
  if (time && isObject<ISetCookieOptions>(time)) {
    domain = time?.domain ?? domain;
    path = time?.path ?? path;
    _time = time?.time ?? time;
    _cookies = time?.cookies?.replace(/;$/, '') ?? _cookies;
    httpOnly = time?.httpOnly ?? httpOnly;
    secure = time?.secure ?? secure;
    sameSite = time?.sameSite ?? sameSite;
    isNeedSetCookies = time?.isNeedSetCookies ?? isNeedSetCookies;
  }

  let str = `${name}=${encodeURIComponent(value)}`;
  if (isNumber(_time)) {
    const date = new Date(_time).toUTCString();
    str += `;expires=${date}`;
  }

  if (isBoolean(httpOnly)) {
    str += '; HttpOnly';
  }

  if (isBoolean(secure)) {
    str += '; Secure';
  }

  if (isString(sameSite)) {
    str += `; SameSite=${sameSite}`;
  }

  if (domain !== 'never') str = domain ? `${str};domain=${domain}` : str;
  str = path ? `${str};path=${path}` : str;

  // 浏览器处理
  if (isBrowser() && isNeedSetCookies) {
    document.cookie = str;
  }

  // 判断是否对象有传入cookies
  if (_cookies) {
    const originValue = getCookie(name, { cookies: _cookies });

    if (!originValue) {
      const arr = _cookies.split(';');
      arr.push(`${name}=${encodeURIComponent(value)}`);
      return arr.join(';');
    }

    // 获取cookies 判断有没有值
    const arr = _cookies.split(';');
    if (arr.length) {
      return arr
        .map(i => {
          if (i.indexOf(`${name}=`) !== -1) {
            const text = i.split('=');

            text[1] = encodeURIComponent(value);

            return text.join('=');
          }
          return i;
        })
        .join(';');
    }
  }
  return str;
}
export interface IGetCookieOptions {
  cookies?: string;
  enableParsing?: boolean;
}
/**
 * @method getCookie  根据cookie的name获取cookie值
 * @param  {string} cookie名
 * @return {string} 返回cookie值
 */
export function getCookie<T>(name: string, options?: IGetCookieOptions): T | undefined {
  const enableParsing = options?.enableParsing ?? true;

  const cookieStr = options?.cookies ?? (isBrowser() ? document.cookie : '');

  if (!cookieStr) {
    loggerError('获取不到cookie 值', LogModule.Cookies);
  }

  let end;
  if (cookieStr.length > 0) {
    let start = cookieStr.indexOf(`${name}=`);
    if (start > -1) {
      start += name.length + 1;
      end = cookieStr.indexOf(';', start);
      if (end === -1) {
        end = cookieStr.length;
      }

      const value = decodeURIComponent(cookieStr.slice(start, end));
      return parseToJSON(value, enableParsing);
    }
  }
  return;
}
/**
 * @method deleteCookie
 * 第二个参数可以以对象的形式传入，time, domain, path, httpOnly, secure, sameSite为属性传入
 * @param  {String} name   cookie key名
 * @param  {String} domain cookie所在的域，默认为请求的地址
 * @param  {String} path   cookie所在的目录，默认为/，就是根目录。
 * @return {void}
 */
export function deleteCookie(name: string, domain = getDomain(), path = '/'): string {
  let _domain = domain;
  let _path = path;
  let _cookies;

  if (domain && isObject<ISetCookieOptions>(domain)) {
    _domain = domain?.domain ?? _domain;
    _path = domain?.path ?? path;
    _cookies = domain?.cookies ?? _cookies;
  }

  const date = new Date('1970/01/01');
  let str = `${name}=null;expires=${date.toUTCString()}`;
  str = _domain ? `${str};domain=${_domain}` : str;
  str = _path ? `${str};path=${_path}` : str;

  if (isBrowser()) {
    document.cookie = str;
  }

  // 判断存在cookies
  if (_cookies) {
    const arr = _cookies.split(';');
    if (arr.length) {
      return arr.filter(i => i.indexOf(`${name}=`) === -1).join(';');
    }
  }
  return str;
}
